<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': isExpanded },
      menuSkin
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li v-if="appConfig.logoName" class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/"
            >
              <span class="brand-logo">
                <b-img
                  :src="require(`@hw-ui/assets/images/logo/${appConfig.logoName}`)"
                  alt="logo"
                />
              </span>
              <h2 class="brand-text">
                {{ appConfig.name }}
              </h2>
            </b-link>
          </li>

          <!--
          when u toggle layout, the layout component gets destroyed and recreated again lead to the dynamic registered module got destroyed as well
          -->
          <li class="nav-item mr-auto">
            <b-link>
              <feather-icon
                icon="ChevronsUpIcon"
                size="20"
                class="mt-2"
                @click="switchLayout"
              />
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="activeMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg } from 'bootstrap-vue'
import { computed, ref } from '@vue/composition-api'
import useLayoutConfig from '@hw-ui/layouts/composables/use-layout-config'

import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import {useVerticalNavMenu} from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    // eslint-disable-next-line vue/require-default-prop, vue/require-prop-types
    menu: {
      required: false,
    },
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  computed: {
    isExpanded () {
      return !this.isVerticalMenuCollapsed || this.isMouseHovered
    },
    menuSkin(){
      return this.skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    },
    appConfig() {
      return this.$store.state.app.appConfig || {}
    },
  },
  setup(props) { // the setup() hook of Composition API is called before any Options API hooks, even beforeCreate().

    const {
      activeMenuItems,
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
      switchLayout,
    } = useVerticalNavMenu(props)

    const { skin } = useLayoutConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // declare reactive state using Reactivity APIs and expose them to the template by returning an object from setup()
    return {
      activeMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIconFeather,
      collapseTogglerIcon,
      isMouseHovered,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // handlers
      updateMouseHovered,
      toggleCollapsed,
      switchLayout,
    }
  },
}
</script>

<style lang="scss">
@import "~@hw-ui/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>
